// src/components/themes/default/store/branch/BranchProductCard.tsx
"use client";

import Image from "next/image";
import { Plus, Check, Zap, Eye, Package } from "lucide-react";
import { useState } from "react";
import { useBranchCart } from "./BranchCartContext";

interface BranchProductCardProps {
  product: any;
  primaryColor?: string;
  onSelectProduct?: (product: any) => void;
  onBuyNowClick?: (product: any) => void;
}

export default function BranchProductCard({
  product,
  primaryColor = "#059669",
  onSelectProduct,
  onBuyNowClick,
}: BranchProductCardProps) {
  const { addToCart } = useBranchCart();
  const [isAdded, setIsAdded] = useState(false);

  const price = Number(product.price || 0);
  const comparePrice = Number(product.compare_price || 0);
  const hasDiscount = comparePrice > price && price > 0;
  const discountPercent =
    product.discount_percentage ||
    (hasDiscount ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0);

  const isInStock = (product.stock_quantity ?? product.stock ?? 1) > 0;
  const imageUrl = product.primary_image || product.image || "";

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: price,
      image: imageUrl,
      stock: product.stock_quantity ?? 10,
    });
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 1500);
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: price,
      image: imageUrl,
      stock: product.stock_quantity ?? 10,
    });
    if (onBuyNowClick) {
      onBuyNowClick(product);
    }
  };

  return (
    <div
      onClick={() => onSelectProduct && onSelectProduct(product)}
      className="group relative bg-white rounded-3xl border border-slate-200/80 p-3.5 flex flex-col justify-between hover:shadow-xl hover:border-emerald-500/40 transition-all duration-300 cursor-pointer"
    >
      <div>
        {/* প্রোডাক্ট ইমেজ ও ব্যাজ */}
        <div className="relative aspect-square w-full rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center p-3 mb-3">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={product.name}
              fill
              unoptimized
              className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <Package className="w-10 h-10 text-slate-300" />
          )}

          {/* ব্যাজ */}
          <div className="absolute top-2.5 left-2.5 flex flex-col gap-1 items-start z-10">
            {hasDiscount && (
              <span className="px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider bg-rose-500 text-white shadow-xs">
                {discountPercent}% OFF
              </span>
            )}
            <span
              className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                isInStock
                  ? "bg-white/95 text-emerald-700 border border-emerald-200 shadow-xs"
                  : "bg-rose-50 text-rose-600 border border-rose-200"
              }`}
            >
              {isInStock ? "In Stock" : "Stock Out"}
            </span>
          </div>

          {/* কুইক ভিউ হোভার ব্যাজ */}
          <div className="absolute inset-0 bg-black/20 backdrop-blur-2xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="px-3 py-1.5 rounded-full bg-white/90 text-slate-800 text-xs font-bold flex items-center gap-1.5 shadow-md">
              <Eye className="w-3.5 h-3.5" />
              <span>View Details</span>
            </span>
          </div>
        </div>

        {/* ক্যাটাগরি ও টাইটেল */}
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-0.5">
          {product.category_name || "General"}
        </span>
        <h3 className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors line-clamp-2 leading-snug">
          {product.name}
        </h3>
      </div>

      {/* প্রাইস ও বাটন */}
      <div className="mt-3 pt-2.5 border-t border-slate-100">
        <div className="flex items-baseline gap-2 mb-2.5">
          <span className="text-base sm:text-lg font-black text-slate-900 font-mono">
            ৳{price.toLocaleString()}
          </span>
          {hasDiscount && (
            <span className="text-xs text-slate-400 line-through font-mono">
              ৳{comparePrice.toLocaleString()}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={!isInStock}
            className={`py-2 px-2 rounded-xl text-[11px] font-bold border transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95 ${
              isAdded
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
            }`}
          >
            {isAdded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            <span>{isAdded ? "Added" : "Add to Cart"}</span>
          </button>

          <button
            type="button"
            onClick={handleBuyNow}
            disabled={!isInStock}
            style={{ backgroundColor: primaryColor }}
            className="py-2 px-2 rounded-xl text-[11px] font-bold text-white shadow-xs hover:opacity-90 transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95"
          >
            <Zap className="w-3 h-3 fill-white" />
            <span>Buy Now</span>
          </button>
        </div>
      </div>
    </div>
  );
}